import { Route, Redirect } from "react-router-dom";
import React, { Suspense, lazy, Fragment } from "react";
import Loader from "react-loaders";

import { ToastContainer } from "react-toastify";
import PrivateRoute from "../../utils/PrivateRoute";
import { isPerformanceMonitoringEnabled } from "../../utils/performanceMonitoringFeatureFlags";

const UserPages = lazy(() => import("../../Pages/UserPages"));
const Applications = lazy(() => import("../../Pages/Applications"));
const Dashboards = lazy(() => import("../../Pages/Dashboards"));
const Permissions = lazy(() => import("../../Pages/App/Permissions"));
const Access = lazy(() => import("../../Pages/App/Access"));
const Blog = lazy(() => import("../../Pages/App/Blog"));
const MapLayers = lazy(() => import("../../Pages/App/MapLayers"));
const Agents = lazy(() => import("../../Pages/App/Agents"));
const Users = lazy(() => import("../../Pages/App/Users"));
const UserInquiries = lazy(() => import("../../Pages/App/UserInquiries"));
const Bids = lazy(() => import("../../Pages/App/Bids"));
const Auctions = lazy(() => import("../../Pages/App/Auctions"));
const CustomFields = lazy(() => import("../../Pages/CustomFields"));
const Roles = lazy(() => import("../../Pages/App/Roles"));
const Agencies = lazy(() => import("../../Pages/App/Agencies"));
const Franchises = lazy(() => import("../../Pages/App/Franchises"));
const Stripe = lazy(() => import("../../Pages/App/Stripe"));
const DiscountHistory = lazy(() => import("../../Pages/App/DiscountHistory"));
const Documents = lazy(() => import("../../Pages/App/Documents"));
const DocumentId = lazy(() => import("../../Pages/App/Documents/DocumentIdPage"));
const CoreFieldsEditor = lazy(() => import("../../Pages/App/CoreFieldsEditor"));
const CompletionFieldsEditor = lazy(() => import("../../Pages/App/CompletionFieldsEditor"));
const LegalDocuments = lazy(() => import("../../Pages/App/LegalDocuments"));
const FeaturedListings = lazy(() => import("../../Pages/App/FeaturedListings"));
const ServiceProviders = lazy(() => import("../../Pages/App/ServiceProviders"));
const CapacityManagement = lazy(() => import("../../Pages/App/Capacity"));
const ServiceProviderPricing = lazy(() => import("../../Pages/App/ServiceProviderPricing"));
const CouponManagement = lazy(() => import("../../Pages/App/CouponManagement"));
const SubscriptionChangeRequests = lazy(() => import("../../Pages/App/SubscriptionChangeRequests"));
const PaymentIssues = lazy(() => import("../../Pages/App/PaymentIssues"));
const UndistributedLeads = lazy(() => import("../../Pages/App/UndistributedLeads"));
const Billing = lazy(() => import("../../Pages/App/Billing"));
const PropertyLabelColors = lazy(() => import("../../Pages/App/PropertyLabelColors"));
const MortgageBranding = lazy(() => import("../../Pages/App/MortgageBranding"));
const FeatureFlags = lazy(() => import("../../Pages/App/FeatureFlags"));
const PerformanceMonitoring = lazy(() => import("../../Pages/App/PerformanceMonitoring"));
const SentryTest = lazy(() => import("../../Pages/SentryTest"));

const Widgets = lazy(() => import("../../Pages/Widgets"));
const Elements = lazy(() => import("../../Pages/Elements"));
const Components = lazy(() => import("../../Pages/Components"));
const Charts = lazy(() => import("../../Pages/Charts"));
const Forms = lazy(() => import("../../Pages/Forms"));
const Tables = lazy(() => import("../../Pages/Tables"));
const Mls = lazy(() => import("../../Pages/MLS"));

const AppMain = () => {
  return (
    <Fragment>
      {/* Components */}

      <Suspense
        fallback={
          <div className="loader-container">
            <div className="loader-container-inner">
              <div className="text-center">
                <Loader type="ball-pulse-rise" />
              </div>
              <h6 className="mt-5">Please wait while we load all the Components</h6>
            </div>
          </div>
        }
      >
        <PrivateRoute path="/components" component={Components} />
      </Suspense>

      <Suspense
        fallback={
          <div className="loader-container">
            <div className="loader-container-inner">
              <div className="text-center">
                <Loader type="ball-grid-cy" />
              </div>
              <h6 className="mt-3">Please wait while we load your page</h6>
            </div>
          </div>
        }
      >
        <Route path="/sentry-test" component={SentryTest} />
      </Suspense>

      {/* Forms */}

      <Suspense
        fallback={
          <div className="loader-container">
            <div className="loader-container-inner">
              <div className="text-center">
                <Loader type="ball-pulse-rise" />
              </div>
              <h6 className="mt-5">
                Please wait while we load all the Forms examples
                <small>
                  Because this is a demonstration we load at once all the Forms examples. This
                  wouldn't happen in a real live app!
                </small>
              </h6>
            </div>
          </div>
        }
      >
        <PrivateRoute path="/forms" component={Forms} />
      </Suspense>

      {/* Charts */}

      <Suspense
        fallback={
          <div className="loader-container">
            <div className="loader-container-inner">
              <div className="text-center">
                <Loader type="ball-rotate" />
              </div>
              <h6 className="mt-3">
                Please wait while we load all the Charts examples
                <small>
                  Because this is a demonstration we load at once all the Charts examples. This
                  wouldn't happen in a real live app!
                </small>
              </h6>
            </div>
          </div>
        }
      >
        <PrivateRoute path="/charts" component={Charts} />
      </Suspense>

      {/* Tables */}

      <Suspense
        fallback={
          <div className="loader-container">
            <div className="loader-container-inner">
              <div className="text-center">
                <Loader type="ball-pulse-rise" />
              </div>
              <h6 className="mt-5">
                Please wait while we load all the Tables examples
                <small>
                  Because this is a demonstration we load at once all the Tables examples. This
                  wouldn't happen in a real live app!
                </small>
              </h6>
            </div>
          </div>
        }
      >
        <PrivateRoute path="/tables" component={Tables} />
      </Suspense>

      {/* Elements */}

      <Suspense
        fallback={
          <div className="loader-container">
            <div className="loader-container-inner">
              <div className="text-center">
                <Loader type="line-scale" />
              </div>
              <h6 className="mt-3">
                Please wait while we load all the Elements examples
                <small>
                  Because this is a demonstration we load at once all the Elements examples. This
                  wouldn't happen in a real live app!
                </small>
              </h6>
            </div>
          </div>
        }
      >
        <PrivateRoute path="/elements" component={Elements} />
      </Suspense>

      {/* Dashboard Widgets */}

      <Suspense
        fallback={
          <div className="loader-container">
            <div className="loader-container-inner">
              <div className="text-center">
                <Loader type="ball-pulse-sync" />
              </div>
              <h6 className="mt-3">
                Please wait while we load all the Dashboard Widgets examples
                <small>
                  Because this is a demonstration we load at once all the Dashboard Widgets
                  examples. This wouldn't happen in a real live app!
                </small>
              </h6>
            </div>
          </div>
        }
      >
        <PrivateRoute path="/widgets" component={Widgets} />
      </Suspense>

      {/* Pages */}

      <Suspense
        fallback={
          <div className="loader-container">
            <div className="loader-container-inner">
              <div className="text-center">
                <Loader type="line-scale-party" />
              </div>
              <h6 className="mt-3">
                Please wait while we load all the Pages examples
                <small>
                  Because this is a demonstration we load at once all the Pages examples. This
                  wouldn't happen in a real live app!
                </small>
              </h6>
            </div>
          </div>
        }
      >
        <PrivateRoute path="/auth" component={UserPages} />
      </Suspense>

      {/* Applications */}

      <Suspense
        fallback={
          <div className="loader-container">
            <div className="loader-container-inner">
              <div className="text-center">
                <Loader type="ball-pulse" />
              </div>
              <h6 className="mt-3">
                Please wait while we load all the Applications examples
                <small>
                  Because this is a demonstration we load at once all the Applications examples.
                  This wouldn't happen in a real live app!
                </small>
              </h6>
            </div>
          </div>
        }
      >
        <PrivateRoute path="/apps" component={Applications} />
      </Suspense>

      {/* Dashboards */}

      <Suspense
        fallback={
          <div className="loader-container">
            <div className="loader-container-inner">
              <div className="text-center">
                <Loader type="ball-grid-cy" />
              </div>
              <h6 className="mt-3">Please wait while we load your page</h6>
            </div>
          </div>
        }
      >
        <PrivateRoute path="/dashboards" component={Dashboards} />
      </Suspense>

      <Suspense
        fallback={
          <div className="loader-container">
            <div className="loader-container-inner">
              <div className="text-center">
                <Loader type="ball-grid-cy" />
              </div>
              <h6 className="mt-3">Please wait while we load your page</h6>
            </div>
          </div>
        }
      >
        <PrivateRoute path="/access" component={Access} />
      </Suspense>
      <Suspense
        fallback={
          <div className="loader-container">
            <div className="loader-container-inner">
              <div className="text-center">
                <Loader type="ball-grid-cy" />
              </div>
              <h6 className="mt-3">Please wait while we load your page</h6>
            </div>
          </div>
        }
      >
        <PrivateRoute path="/blog" component={Blog} />
      </Suspense>
      <Suspense
        fallback={
          <div className="loader-container">
            <div className="loader-container-inner">
              <div className="text-center">
                <Loader type="ball-grid-cy" />
              </div>
              <h6 className="mt-3">Please wait while we load your page</h6>
            </div>
          </div>
        }
      >
        <PrivateRoute path="/map-layers" component={MapLayers} />
      </Suspense>
      <Suspense
        fallback={
          <div className="loader-container">
            <div className="loader-container-inner">
              <div className="text-center">
                <Loader type="ball-grid-cy" />
              </div>
              <h6 className="mt-3">Please wait while we load your page</h6>
            </div>
          </div>
        }
      >
        <PrivateRoute path="/agents" component={Agents} />
      </Suspense>
      <Suspense
        fallback={
          <div className="loader-container">
            <div className="loader-container-inner">
              <div className="text-center">
                <Loader type="ball-grid-cy" />
              </div>
              <h6 className="mt-3">Please wait while we load your page</h6>
            </div>
          </div>
        }
      >
        <PrivateRoute path="/auctions" component={Auctions} />
      </Suspense>
      <Suspense
        fallback={
          <div className="loader-container">
            <div className="loader-container-inner">
              <div className="text-center">
                <Loader type="ball-grid-cy" />
              </div>
              <h6 className="mt-3">Please wait while we load your page</h6>
            </div>
          </div>
        }
      >
        <PrivateRoute path="/bids" component={Bids} />
      </Suspense>
      <Suspense
        fallback={
          <div className="loader-container">
            <div className="loader-container-inner">
              <div className="text-center">
                <Loader type="ball-grid-cy" />
              </div>
              <h6 className="mt-3">Please wait while we load your page</h6>
            </div>
          </div>
        }
      >
        <PrivateRoute path="/users" component={Users} />
      </Suspense>
      <Suspense
        fallback={
          <div className="loader-container">
            <div className="loader-container-inner">
              <div className="text-center">
                <Loader type="ball-grid-cy" />
              </div>
              <h6 className="mt-3">Please wait while we load your page</h6>
            </div>
          </div>
        }
      >
        <PrivateRoute path="/user-inquiries" component={UserInquiries} />
      </Suspense>
      <Suspense
        fallback={
          <div className="loader-container">
            <div className="loader-container-inner">
              <div className="text-center">
                <Loader type="ball-grid-cy" />
              </div>
              <h6 className="mt-3">Please wait while we load your page</h6>
            </div>
          </div>
        }
      >
        <PrivateRoute path="/permissions" component={Permissions} />
      </Suspense>
      <Suspense
        fallback={
          <div className="loader-container">
            <div className="loader-container-inner">
              <div className="text-center">
                <Loader type="ball-grid-cy" />
              </div>
              <h6 className="mt-3">Please wait while we load your page</h6>
            </div>
          </div>
        }
      >
        <PrivateRoute path="/roles" component={Roles} />
      </Suspense>
      <Suspense
        fallback={
          <div className="loader-container">
            <div className="loader-container-inner">
              <div className="text-center">
                <Loader type="ball-grid-cy" />
              </div>
              <h6 className="mt-3">Please wait while we load your page</h6>
            </div>
          </div>
        }
      >
        <PrivateRoute path="/agencies" component={Agencies} />
      </Suspense>
      <Suspense
        fallback={
          <div className="loader-container">
            <div className="loader-container-inner">
              <div className="text-center">
                <Loader type="ball-grid-cy" />
              </div>
              <h6 className="mt-3">Please wait while we load your page</h6>
            </div>
          </div>
        }
      >
        <PrivateRoute path="/franchises" component={Franchises} />
      </Suspense>
      <Suspense
        fallback={
          <div className="loader-container">
            <div className="loader-container-inner">
              <div className="text-center">
                <Loader type="ball-grid-cy" />
              </div>
              <h6 className="mt-3">Please wait while we load your page</h6>
            </div>
          </div>
        }
      >
        <PrivateRoute path="/payments" component={Stripe} />
        <PrivateRoute path="/discount-history" component={DiscountHistory} />
      </Suspense>

      <Suspense
        fallback={
          <div className="loader-container">
            <div className="loader-container-inner">
              <div className="text-center">
                <Loader type="ball-grid-cy" />
              </div>
              <h6 className="mt-3">Please wait while we load your page</h6>
            </div>
          </div>
        }
      >
        <Route path="/custom-fields" component={CustomFields} />
      </Suspense>

      <Suspense
        fallback={
          <div className="loader-container">
            <div className="loader-container-inner">
              <div className="text-center">
                <Loader type="ball-grid-cy" />
              </div>
              <h6 className="mt-3">Please wait while we load your page</h6>
            </div>
          </div>
        }
      >
        <Route path="/property-label-colors" component={PropertyLabelColors} />
      </Suspense>

      <Suspense
        fallback={
          <div className="loader-container">
            <div className="loader-container-inner">
              <div className="text-center">
                <Loader type="ball-grid-cy" />
              </div>
              <h6 className="mt-3">Please wait while we load your page</h6>
            </div>
          </div>
        }
      >
        <Route path="/mls" component={Mls} />
      </Suspense>

      <Suspense
        fallback={
          <div className="loader-container">
            <div className="loader-container-inner">
              <div className="text-center">
                <Loader type="ball-grid-cy" />
              </div>
              <h6 className="mt-3">Please wait while we load your page</h6>
            </div>
          </div>
        }
      >
        <Route exact path="/documents" component={Documents} />
      </Suspense>

      <Suspense
        fallback={
          <div className="loader-container">
            <div className="loader-container-inner">
              <div className="text-center">
                <Loader type="ball-grid-cy" />
              </div>
              <h6 className="mt-3">Please wait while we load your page</h6>
            </div>
          </div>
        }
      >
        <Route exact path="/core-fields" component={CoreFieldsEditor} />
        <Route exact path="/completion-fields" component={CompletionFieldsEditor} />
      </Suspense>

      <Suspense
        fallback={
          <div className="loader-container">
            <div className="loader-container-inner">
              <div className="text-center">
                <Loader type="ball-grid-cy" />
              </div>
              <h6 className="mt-3">Please wait while we load your page</h6>
            </div>
          </div>
        }
      >
        <Route exact path="/legal-documents" component={LegalDocuments} />
      </Suspense>

      <Suspense
        fallback={
          <div className="loader-container">
            <div className="loader-container-inner">
              <div className="text-center">
                <Loader type="ball-grid-cy" />
              </div>
              <h6 className="mt-3">Please wait while we load your page</h6>
            </div>
          </div>
        }
      >
        <Route path="/documents/:id" component={DocumentId} />
      </Suspense>

      <Suspense
        fallback={
          <div className="loader-container">
            <div className="loader-container-inner">
              <div className="text-center">
                <Loader type="ball-grid-cy" />
              </div>
              <h6 className="mt-3">Please wait while we load your page</h6>
            </div>
          </div>
        }
      >
        <Route path="/featured-listings" component={FeaturedListings} />
      </Suspense>

      <Suspense
        fallback={
          <div className="loader-container">
            <div className="loader-container-inner">
              <div className="text-center">
                <Loader type="ball-grid-cy" />
              </div>
              <h6 className="mt-3">Please wait while we load your page</h6>
            </div>
          </div>
        }
      >
        <PrivateRoute path="/service-providers" component={ServiceProviders} />
        <PrivateRoute path="/capacity-management" component={CapacityManagement} />
        <PrivateRoute path="/service-provider-pricing" component={ServiceProviderPricing} />
        <PrivateRoute path="/mortgage-calculator-branding" component={MortgageBranding} />
        <PrivateRoute path="/feature-flags" component={FeatureFlags} />
        {isPerformanceMonitoringEnabled() ? (
          <PrivateRoute path="/performance-monitoring" component={PerformanceMonitoring} />
        ) : (
          <Route
            path="/performance-monitoring"
            render={() => <Redirect to="/agents" />}
          />
        )}
      </Suspense>

      <Suspense
        fallback={
          <div className="loader-container">
            <div className="loader-container-inner">
              <div className="text-center">
                <Loader type="ball-grid-cy" />
              </div>
              <h6 className="mt-3">Please wait while we load your page</h6>
            </div>
          </div>
        }
      >
        <PrivateRoute path="/coupon-management" component={CouponManagement} />
      </Suspense>

      <Suspense
        fallback={
          <div className="loader-container">
            <div className="loader-container-inner">
              <div className="text-center">
                <Loader type="ball-grid-cy" />
              </div>
              <h6 className="mt-3">Please wait while we load your page</h6>
            </div>
          </div>
        }
      >
        <PrivateRoute path="/subscription-change-requests" component={SubscriptionChangeRequests} />
      </Suspense>

      <Suspense
        fallback={
          <div className="loader-container">
            <div className="loader-container-inner">
              <div className="text-center">
                <Loader type="ball-grid-cy" />
              </div>
              <h6 className="mt-3">Please wait while we load your page</h6>
            </div>
          </div>
        }
      >
        <PrivateRoute path="/payment-issues" component={PaymentIssues} />
      </Suspense>

      <Suspense
        fallback={
          <div className="loader-container">
            <div className="loader-container-inner">
              <div className="text-center">
                <Loader type="ball-grid-cy" />
              </div>
              <h6 className="mt-3">Please wait while we load your page</h6>
            </div>
          </div>
        }
      >
        <PrivateRoute path="/undistributed-leads" component={UndistributedLeads} />
      </Suspense>

      <Suspense
        fallback={
          <div className="loader-container">
            <div className="loader-container-inner">
              <div className="text-center">
                <Loader type="ball-grid-cy" />
              </div>
              <h6 className="mt-3">Please wait while we load your page</h6>
            </div>
          </div>
        }
      >
        <PrivateRoute path="/billing" component={Billing} />
      </Suspense>

      <Route exact path="/" render={() => <Redirect to="/agents" />} />
      <ToastContainer />
    </Fragment>
  );
};

export default AppMain;
